import { cn } from '@lib/cn';
import { Badge, IconWarning, type BadgeTone } from '@shared/ui';

import { FEATURE_STATE_LABEL } from '../model/museum-detail';
import { STATUS_LABEL } from '../model/museum-list';

import type { FeatureState, MuseumStatus } from '../schemas/activation';

/**
 * The two chips of these screens: a museum's status and a feature's state.
 *
 * Tones are fixed by the design, and the one worth explaining is "Couldn't
 * check" in red: it is not the museum's problem, it is the page failing to find
 * out — so it must not share the amber of "Not done", which is. It is also the
 * only SOLID chip (red fill, white text), as the design's `variant: 'solid'`:
 * the one state an operator must not scan past.
 *
 * The design draws these chips in regular weight; the kit's `Badge` is
 * semibold for every other screen, so the weight is overridden here, locally.
 * `!` because `cn` only joins: without it, which of two `font-*` wins is
 * stylesheet order.
 */

const CHIP = '!font-normal';

/** Red fill, white text. `text-white` is literal on purpose, like the kit's red solids. */
const SOLID_DANGER = '!border-transparent !bg-danger-solid !text-white';

const MUSEUM_TONE: Record<MuseumStatus, BadgeTone> = {
    not_started: 'neutral',
    in_progress: 'info',
    completed: 'success',
};

const FEATURE_TONE: Record<FeatureState, BadgeTone> = {
    done: 'success',
    not_done: 'warning',
    unknown: 'danger',
    no_data: 'neutral',
};

export function MuseumStatusBadge({ status }: { status: MuseumStatus }) {
    return (
        <Badge tone={MUSEUM_TONE[status]} dot className={CHIP}>
            {STATUS_LABEL[status]}
        </Badge>
    );
}

export function FeatureStateBadge({ state }: { state: FeatureState }) {
    if (state === 'unknown') {
        return (
            <Badge tone={FEATURE_TONE[state]} className={cn(CHIP, SOLID_DANGER)}>
                <IconWarning className="size-3" />
                {FEATURE_STATE_LABEL[state]}
            </Badge>
        );
    }

    // "No data" is an outline: nothing was measured, so it gets no fill and
    // no dot — the two things every measured state has. `!` because `cn` only
    // joins: without it, which of two `bg-*` wins is stylesheet order.
    if (state === 'no_data') {
        return (
            <Badge tone={FEATURE_TONE[state]} className={cn(CHIP, '!bg-transparent')}>
                {FEATURE_STATE_LABEL[state]}
            </Badge>
        );
    }

    return (
        <Badge tone={FEATURE_TONE[state]} dot className={CHIP}>
            {FEATURE_STATE_LABEL[state]}
        </Badge>
    );
}
